import React from "react";
import { fmtDate, labelize } from "@/lib/constants";
import { ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { InfoTile } from "./LeadInfoTiles";

/** Fields the user can edit inline on the Overview tab (order matters). */
const EDITABLE_FIELDS = [
  "first_name", "last_name", "phone", "email", "address", "city",
  "state", "zip", "county", "accident_date", "notes", "trustedform_url",
  "sub_1", "sub_2", "sub_3", "sub_4", "sub_5",
];

/**
 * Consumer-panel field spec. `testId` is optional (only the top 4 get one for
 * regression tests). Adding a new consumer field = add one row here.
 */
const CONSUMER_FIELDS = [
  { name: "first_name",    label: "First name",    testId: "edit-first" },
  { name: "last_name",     label: "Last name",     testId: "edit-last" },
  { name: "phone",         label: "Phone",         testId: "edit-phone" },
  { name: "email",         label: "Email",         testId: "edit-email" },
  { name: "address",       label: "Address" },
  { name: "city",          label: "City" },
  { name: "state",         label: "State" },
  { name: "zip",           label: "ZIP" },
  { name: "county",        label: "County" },
  { name: "accident_date", label: "Accident date", type: "date" },
];

function ConsumerField({ spec, editing, edit, setEdit, orig }) {
  if (!editing) {
    return <InfoTile k={spec.label} v={orig[spec.name] || "—"} />;
  }
  return (
    <InfoTile
      k={spec.label}
      v={
        <input
          data-testid={spec.testId}
          type={spec.type || "text"}
          value={edit[spec.name] || ""}
          onChange={(e) => setEdit({ ...edit, [spec.name]: e.target.value })}
          className="border border-black px-2 py-1 w-full"
        />
      }
    />
  );
}

function ConsumerCard({ lead, editing, edit, setEdit, editActions }) {
  return (
    <div className="brut-card p-5 lg:col-span-2">
      <div className="font-chivo font-bold uppercase tracking-tight mb-3">Consumer</div>
      <div className="grid grid-cols-2 gap-3 text-sm font-mono">
        {CONSUMER_FIELDS.map((spec) => (
          <ConsumerField
            key={spec.name}
            spec={spec}
            editing={editing}
            edit={edit}
            setEdit={setEdit}
            orig={lead}
          />
        ))}
      </div>
      {editing && editActions}
    </div>
  );
}

function EditActions({ reason, setReason, share, setShare, onCancel, onSave }) {
  return (
    <div className="mt-4 pt-4 border-t border-black space-y-3">
      <div>
        <label className="label-mono">Reason for change</label>
        <input data-testid="edit-reason" value={reason} onChange={(e) => setReason(e.target.value)}
          className="w-full border border-black px-2 py-1.5 mt-1"
          placeholder="e.g. corrected typo in phone number" />
      </div>
      <div className="flex items-center gap-4 text-xs uppercase tracking-widest">
        <label className="inline-flex items-center gap-2">
          <input data-testid="edit-share-client" type="checkbox" checked={share.client}
            onChange={(e) => setShare({ ...share, client: e.target.checked })} /> Share w/ client
        </label>
        <label className="inline-flex items-center gap-2">
          <input data-testid="edit-share-affiliate" type="checkbox" checked={share.affiliate}
            onChange={(e) => setShare({ ...share, affiliate: e.target.checked })} /> Share w/ affiliate
        </label>
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="border border-black px-3 py-1.5 text-xs uppercase tracking-widest">Cancel</button>
        <button onClick={onSave} data-testid="edit-save"
          className="bg-black text-white border border-black px-3 py-1.5 text-xs uppercase tracking-widest">
          Save changes
        </button>
      </div>
    </div>
  );
}

const BADGE = "inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest";

function ClaimBadges({ lead, isInternal }) {
  const claim = lead.trustedform_claim;
  return (
    <>
      {lead.trustedform_match_status === "mismatch" && (
        <span className={`${BADGE} border-red-700 bg-red-50 text-red-700`} data-testid="tf-mismatch-badge"
              title="TrustedForm says the email or phone on this lead doesn't match the certificate">
          <WarningCircle size={11} weight="bold" /> Doesn&apos;t match
        </span>
      )}
      {isInternal && claim?.status === "retained" && (
        <span className={`${BADGE} border-emerald-700 bg-emerald-50 text-emerald-800`} data-testid="tf-claim-retained"
              title={claim.expires_at ? `TrustedForm expiry reported: ${new Date(claim.expires_at).toLocaleString()}` : ""}>
          Claimed in TrustedForm
        </span>
      )}
      {isInternal && ["retrying", "failed"].includes(claim?.status) && (
        <span className={`${BADGE} border-amber-700 bg-amber-50 text-amber-800`} data-testid={`tf-claim-${claim.status}`} title={claim.reason || ""}>
          {claim.status === "retrying" ? "Claim retrying" : "Claim failed"}
        </span>
      )}
    </>
  );
}

function TrustedFormValue({ lead, isInternal, onVerify, verifying }) {
  if (!lead.trustedform_url) {
    return (
      <span className="inline-flex items-center gap-1 border border-red-700 bg-red-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-red-700" data-testid="tf-missing-badge">
        <WarningCircle size={11} weight="bold" /> No certificate
      </span>
    );
  }
  const status = lead.trustedform_status || "captured";
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      {/^https:\/\/cert\.trustedform\.com\//.test(lead.trustedform_url)
        ? <a href={lead.trustedform_url} target="_blank" rel="noopener noreferrer" className="underline" data-testid="tf-cert-link">{status}</a>
        : <span data-testid="tf-status">{status}</span>}
      {isInternal && (
        <button onClick={onVerify} disabled={verifying} data-testid="tf-verify-btn"
          className="border border-black px-2 py-0.5 text-[10px] uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-60">
          <ShieldCheck size={10} className="inline" /> {verifying ? "…" : "Verify"}
        </button>
      )}
      <ClaimBadges lead={lead} isInternal={isInternal} />
    </span>
  );
}

function MetadataCard({ data, onVerifyTf, verifying, isInternal, isClientUser }) {
  const l = data.lead;
  const subIds = [l.sub_1, l.sub_2, l.sub_3, l.sub_4, l.sub_5].filter(Boolean).join(" · ") || "—";
  return (
    <div className="brut-card p-5">
      <div className="font-chivo font-bold uppercase tracking-tight mb-3">Metadata</div>
      <div className="text-sm font-mono space-y-2">
        <InfoTile k="Intake" v={labelize(l.intake_method)} />
        <InfoTile k="External ID" v={l.external_lead_id || "—"} />
        {isInternal && <InfoTile k="Client" v={data.client?.name || "—"} />}
        {isInternal && <InfoTile k="Affiliate" v={data.affiliate?.name || "—"} />}
        {isClientUser && <InfoTile k="Source" v={l.public_source_id || "—"} />}
        <InfoTile k="Campaign" v={data.campaign?.name || "—"} />
        <InfoTile k="TrustedForm" v={
          <TrustedFormValue lead={l} isInternal={isInternal}
                            onVerify={onVerifyTf} verifying={verifying} />
        } />
        <InfoTile k="Sub IDs" v={subIds} />
        <InfoTile k="Received" v={fmtDate(l.created_at)} />
        <InfoTile k="Updated" v={fmtDate(l.updated_at)} />
      </div>
    </div>
  );
}

/**
 * Consumer + metadata card for the LeadDetail Overview tab.
 * When `editing=true` the consumer half becomes an inline edit form.
 */
export default function LeadOverview({
  data, editing, edit, setEdit, reason, setReason, share, setShare,
  onCancel, onSave, onVerifyTf, verifying, isInternal, isClientUser,
}) {
  const editActions = (
    <EditActions
      reason={reason} setReason={setReason}
      share={share} setShare={setShare}
      onCancel={onCancel} onSave={onSave}
    />
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
      <ConsumerCard
        lead={data.lead}
        editing={editing} edit={edit} setEdit={setEdit}
        editActions={editActions}
      />
      <MetadataCard
        data={data}
        onVerifyTf={onVerifyTf} verifying={verifying}
        isInternal={isInternal} isClientUser={isClientUser}
      />
    </div>
  );
}

export { EDITABLE_FIELDS };
